import { ROUTE_QUESTIONNAIRE_V2 } from '@/constants/routes';
import { Box, BoxProps, IconArrowRight, Link, LinkSize, Text, TextProps, useBreakpointValue } from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';

export type IncompleteAssessmentVariant = 'mobileNavBar' | 'large' | 'small';

type IncompleteAssessmentProps = {
  assessmentProgress?: number;
  variant: IncompleteAssessmentVariant;
};

function useLinkSize(variant: IncompleteAssessmentVariant): LinkSize {
  const locationSize: LinkSize = useBreakpointValue({ base: 'm', xl: 'l' }, { ssr: false }) ?? 'm';
  const linkSize: LinkSize = variant === 'mobileNavBar' ? 's' : locationSize;
  return linkSize;
}

function getContainerProps(variant: IncompleteAssessmentVariant): BoxProps {
  switch (variant) {
    case 'mobileNavBar':
      return {
        px: 'spacer-5',
        py: 'spacer-4',
        width: '100%',
        textAlign: 'left',
      };
    case 'small':
      return {
        px: { base: 'spacer-6', xl: 'spacer-8' },
        py: { base: 'spacer-6', xl: 'spacer-9' },
        width: { base: '100%', lg: '320px', xl: '360px', xxl: '400px' },
        textAlign: 'left',
      };
    case 'large':
      return {
        px: {
          base: 'spacer-6',
          lg: 'spacer-40',
          xl: 'spacer-64',
          xxl: 'spacer-80',
        },
        py: { base: 'spacer-6', lg: 'spacer-9' },
        width: '100%',
        textAlign: { base: 'left', md: 'center' },
      };
  }
}

function getHeadlineProps(variant: IncompleteAssessmentVariant): TextProps {
  switch (variant) {
    case 'mobileNavBar':
      return {
        variant: 'text-body-floating-label-medium',
        mb: 'spacer-half',
      };
    case 'small':
    case 'large':
      return {
        variant: 'text-header-XS',
        mb: 'spacer-2',
      };
  }
}

export default function IncompleteAssessment({ variant }: IncompleteAssessmentProps) {
  const linkSize = useLinkSize(variant);

  return (
    <Box {...getContainerProps(variant)} borderRadius="radii-4" backgroundColor="alert-error-default">
      <Text {...getHeadlineProps(variant)} textTransform="uppercase">
        incomplete assessment
      </Text>
      {variant !== 'mobileNavBar' && (
        <Text
          variant="text-body-regular"
          mb={variant === 'small' ? { base: 'spacer-5', lg: 'spacer-6' } : { base: 'spacer-5', xl: 'spacer-6' }}
        >
          You haven't completed your risk assessment yet. Please complete your risk assessment to calculate your risk
          score and receive your personalized action plan.
        </Text>
      )}
      <Link
        as={ReactRouterLink}
        to={ROUTE_QUESTIONNAIRE_V2}
        variant="primary"
        size={linkSize}
        trailingIcon={<IconArrowRight />}
        width={variant === 'mobileNavBar' ? '100%' : 'auto'}
        justifyContent="center"
      >
        Complete Assessment
      </Link>
    </Box>
  );
}
